/**
 * NextActionsStrip — fixed-height (120px) horizontal strip of recommended
 * next actions, rendered below the split pane. Renders nothing when there
 * are no derivable cards.
 */

import { AlertTriangle, FileEdit, Sparkles, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import type { NextActionCard } from './types';

interface Props {
  cards: NextActionCard[];
}

const CARD_TYPES: Record<NextActionCard['type'], { icon: ReactNode; color: string }> = {
  iterate: {
    icon: <Zap size={14} />,
    color: 'border-teal-200 bg-teal-50 hover:border-teal-300',
  },
  rev_brief: {
    icon: <FileEdit size={14} />,
    color: 'border-blue-200 bg-blue-50 hover:border-blue-300',
  },
  generate_more: {
    icon: <Sparkles size={14} />,
    color: 'border-purple-200 bg-purple-50 hover:border-purple-300',
  },
  fatigue_warning: {
    icon: <AlertTriangle size={14} />,
    color: 'border-amber-200 bg-amber-50 hover:border-amber-300',
  },
};

export function NextActionsStrip({ cards }: Props) {
  if (cards.length === 0) return null;
  return (
    <div className="border-t border-slate-200 bg-white px-5 py-3" style={{ minHeight: 120 }}>
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2">
        Recommended Next Steps
      </p>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {cards.map((card, idx) => {
          const cfg = CARD_TYPES[card.type];
          return (
            <button
              key={idx}
              type="button"
              onClick={card.onAction}
              className={clsx(
                'flex-shrink-0 text-left rounded-xl border p-3 min-w-[200px] max-w-[260px] space-y-1.5 transition-colors',
                cfg.color,
              )}
            >
              <div className="flex items-center gap-1.5 text-slate-700">
                {cfg.icon}
                <p className="text-xs font-semibold leading-snug">{card.title}</p>
              </div>
              <p
                className="text-[11px] text-slate-600 leading-relaxed line-clamp-2"
                title={card.description}
              >
                {card.description}
              </p>
              <div className="flex items-center justify-between">
                {card.estimatedImpact && (
                  <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
                    {card.estimatedImpact}
                  </span>
                )}
                <span className="text-[11px] font-semibold text-teal-600 ml-auto">
                  {card.cta}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
